import { isTabletDown } from '../Utility/Breakpoint'
import { getElement, getElementChild, getElements } from '../Utility/Dom'
import { IS_ACTIVE } from '../Utility/State'

interface PanelHistoryEntry {
  panelId: string
  trigger: HTMLElement | null
}

class NavMainMobile {
  private readonly backButtons: NodeListOf<HTMLElement>
  private readonly breadcrumbButtons: NodeListOf<HTMLElement>
  private readonly navButton: HTMLElement | null
  private panelHistory: PanelHistoryEntry[] = []
  private readonly panels: NodeListOf<HTMLElement>
  private readonly panelTriggers: NodeListOf<HTMLElement>
  private readonly topBar: HTMLElement | null

  constructor() {
    this.navButton = getElement('nav-button')
    this.panels = getElements('nav-panel')
    this.panelTriggers = document.querySelectorAll('[data-panel-target]')
    this.backButtons = getElements('nav-panel-back')
    this.breadcrumbButtons = getElements('nav-main-breadcrumb-button')
    this.topBar = getElement('top-bar')
  }

  public init(): void {
    this.addEvents()
    this.updatePanelInertState()
  }

  private addEvents(): void {
    this.navButton?.addEventListener('click', (): void => {
      this.toggleNav()
    })

    this.panelTriggers.forEach((trigger): void => {
      trigger.addEventListener('click', (event): void => {
        if (!isTabletDown()) return

        event.preventDefault()
        const targetId = trigger.getAttribute('data-panel-target')
        if (targetId) {
          this.navigateToPanel(targetId, trigger as HTMLElement)
        }
      })
    })

    this.backButtons.forEach((button): void => {
      button.addEventListener('click', (): void => {
        this.navigateBack()
      })
    })

    this.breadcrumbButtons.forEach((button): void => {
      button.addEventListener('click', (): void => {
        if (!isTabletDown()) return

        const targetId = button.getAttribute('data-back-target')
        if (targetId) {
          this.navigateToBreadcrumbTarget(targetId)
        } else {
          this.navigateToLevel1()
        }
      })
    })

    document.addEventListener('keydown', (event): void => {
      if (event.key === 'Escape' && this.isNavOpen()) {
        this.closeNav()
      }
    })

    window.addEventListener('resize', (): void => {
      if (!isTabletDown() && this.isNavOpen()) {
        this.closeNav()
      }
    })
  }

  private closeNav(): void {
    this.navButton?.setAttribute('aria-expanded', 'false')
    this.resetToLevel1()
    this.updatePanelInertState()
    this.setPageInert(false)
    this.navButton?.focus()
  }

  private focusFirstElement(container: HTMLElement | null): void {
    if (!container) return

    const focusable = container.querySelector<HTMLElement>(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    )
    focusable?.focus()
  }

  private getActivePanel(): HTMLElement | null {
    for (const panel of this.panels) {
      if (panel.classList.contains(IS_ACTIVE) && panel.getAttribute('data-level') !== '1') {
        return panel
      }
    }
    return this.getLevel1Panel()
  }

  private getLevel1Panel(): HTMLElement | null {
    return document.querySelector('[data-js="nav-panel"][data-level="1"]')
  }

  private isNavOpen(): boolean {
    return this.navButton?.getAttribute('aria-expanded') === 'true'
  }

  private navigateBack(): void {
    const currentPanel = this.getActivePanel()
    if (currentPanel) {
      currentPanel.classList.remove(IS_ACTIVE)
    }

    const previousEntry = this.panelHistory.pop()

    if (previousEntry && previousEntry.panelId !== 'level-1') {
      const previousPanel = document.getElementById(previousEntry.panelId)
      if (previousPanel) {
        previousPanel.classList.add(IS_ACTIVE)
        this.updatePanelInertState()

        requestAnimationFrame((): void => {
          if (previousEntry.trigger) {
            previousEntry.trigger.focus()
          } else {
            this.focusFirstElement(previousPanel)
          }
        })
        return
      }
    }

    this.resetToLevel1()

    requestAnimationFrame((): void => {
      if (previousEntry?.trigger) {
        previousEntry.trigger.focus()
      } else {
        const level1Panel = this.getLevel1Panel()
        this.focusFirstElement(level1Panel)
      }
    })
  }

  private navigateToBreadcrumbTarget(targetId: string): void {
    const targetPanel = document.getElementById(targetId)
    if (!targetPanel) return

    const currentPanel = this.getActivePanel()
    if (currentPanel) {
      currentPanel.classList.remove(IS_ACTIVE)
    }

    while (this.panelHistory.length > 0) {
      const entry = this.panelHistory[this.panelHistory.length - 1]
      if (entry.panelId === targetId) {
        this.panelHistory.pop()
        break
      }
      this.panelHistory.pop()
    }

    targetPanel.classList.add(IS_ACTIVE)
    this.updatePanelInertState()

    requestAnimationFrame((): void => {
      this.focusFirstElement(targetPanel)
    })
  }

  private navigateToLevel1(): void {
    const currentPanel = this.getActivePanel()
    if (currentPanel) {
      currentPanel.classList.remove(IS_ACTIVE)
    }

    this.resetToLevel1()

    requestAnimationFrame((): void => {
      const level1Panel = this.getLevel1Panel()
      this.focusFirstElement(level1Panel)
    })
  }

  private navigateToPanel(targetId: string, trigger: HTMLElement): void {
    const targetPanel = document.getElementById(targetId)
    if (!targetPanel) return

    const currentPanel = this.getActivePanel()
    if (currentPanel) {
      this.panelHistory.push({
        panelId: currentPanel.id || 'level-1',
        trigger
      })
      currentPanel.classList.remove(IS_ACTIVE)
    }

    targetPanel.classList.add(IS_ACTIVE)
    this.updatePanelInertState()

    requestAnimationFrame((): void => {
      const backButton = getElementChild(targetPanel, 'nav-panel-back')
      if (backButton) {
        backButton.focus()
      } else {
        this.focusFirstElement(targetPanel)
      }
    })
  }

  private openNav(): void {
    // Avoid gaps when a user scrolls the page minimal and clicks the nav button
    if (this.topBar !== null && this.topBar.classList.contains(IS_ACTIVE) === false) {
      window.scrollTo({ behavior: 'instant', top: 0 })
    }

    this.navButton?.setAttribute('aria-expanded', 'true')
    this.updatePanelInertState()
    this.setPageInert(true)

    requestAnimationFrame((): void => {
      const level1Panel = this.getLevel1Panel()
      this.focusFirstElement(level1Panel)
    })
  }

  private resetToLevel1(): void {
    this.panels.forEach((panel): void => {
      if (panel.getAttribute('data-level') === '1') {
        panel.classList.add(IS_ACTIVE)
      } else {
        panel.classList.remove(IS_ACTIVE)
      }
    })

    this.panelHistory = []
    this.updatePanelInertState()
  }

  // Make page content inert when nav is open to trap focus
  private setPageInert(inert: boolean): void {
    const nav = getElement('nav-main')
    const inertElements = document.querySelectorAll('main, footer, [data-inert]')

    inertElements.forEach((element): void => {
      if (inert) {
        element.setAttribute('inert', '')
      } else {
        element.removeAttribute('inert')
      }
    })

    const header = nav?.closest('header')
    if (header) {
      Array.from(header.children).forEach((child): void => {
        if (child !== nav && !child.contains(nav)) {
          if (inert) {
            child.setAttribute('inert', '')
          } else {
            child.removeAttribute('inert')
          }
        }
      })
    }
  }

  private toggleNav(): void {
    if (this.isNavOpen()) {
      this.closeNav()
    } else {
      this.openNav()
    }
  }

  private updatePanelInertState(): void {
    if (!isTabletDown()) {
      this.panels.forEach((panel): void => {
        panel.removeAttribute('inert')
      })
      return
    }

    this.panels.forEach((panel): void => {
      if (panel.classList.contains(IS_ACTIVE)) {
        panel.removeAttribute('inert')
      } else {
        panel.setAttribute('inert', '')
      }
    })
  }
}

const navMainMobile = new NavMainMobile()
navMainMobile.init()
